import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import {
  FileText, ClipboardList, Users, Receipt, Banknote, Search, Loader2, CornerDownLeft,
} from "lucide-react";
import { eur, fmtDate, nlStatus } from "../../lib/format";
import api from "../../lib/api";

interface SearchResult {
  type: "invoice" | "quote" | "contact" | "expense" | "payment";
  id: string;
  title: string;
  subtitle: string;
  amount?: number;
  currency?: string;
  status?: string;
  date?: string | null;
}

const TYPE_META: Record<SearchResult["type"], { label: string; icon: typeof FileText; path: string; color: string }> = {
  invoice: { label: "Facturen", icon: FileText, path: "/invoices", color: "bg-brand-50 text-brand-600" },
  quote: { label: "Offertes", icon: ClipboardList, path: "/quotes", color: "bg-indigo-50 text-indigo-600" },
  contact: { label: "Contacten", icon: Users, path: "/contacts", color: "bg-emerald-50 text-emerald-600" },
  expense: { label: "Uitgaven", icon: Receipt, path: "/expenses", color: "bg-rose-50 text-rose-600" },
  payment: { label: "Betalingen", icon: Banknote, path: "/payments", color: "bg-amber-50 text-amber-600" },
};

const TYPE_ORDER: SearchResult["type"][] = ["invoice", "quote", "contact", "expense", "payment"];

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Globaal zoekvenster (Topbar-vergrootglas / Ctrl+K). Zoekt door facturen,
 *  offertes, contacten, uitgaven en betalingen van het actieve bedrijf via
 *  GET /api/search. Pijltjes + Enter of klik → naar de bijbehorende pagina. */
export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Reset bij openen + focus het invoerveld.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults([]);
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Gedebouncteerd zoeken.
  useEffect(() => {
    if (!isOpen) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const { data } = await api.get("/api/search", { params: { q }, signal: ctrl.signal });
        setResults(Array.isArray(data?.results) ? data.results : []);
        setActiveIndex(0);
      } catch {
        // Afgebroken of mislukt request: resultaten gewoon laten staan.
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, isOpen]);

  // Groepeer resultaten per soort (volgens TYPE_ORDER).
  const grouped = useMemo(() => {
    const byType = new Map<SearchResult["type"], SearchResult[]>();
    for (const r of results) {
      if (!byType.has(r.type)) byType.set(r.type, []);
      byType.get(r.type)!.push(r);
    }
    return TYPE_ORDER.filter((t) => byType.has(t)).map((t) => ({ type: t, items: byType.get(t)! }));
  }, [results]);

  // Platte lijst voor pijltjes-navigatie.
  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  const go = (r: SearchResult) => {
    onClose();
    navigate(TYPE_META[r.type].path);
  };

  // Pijltjes + Enter + Escape.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = flat[activeIndex];
      if (r) go(r);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  // Houd het actieve resultaat in beeld.
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${activeIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  let idx = -1;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center pt-[12vh] p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95 translate-y-2"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-2"
            >
              <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white shadow-2xl">
                {/* Zoekbalk */}
                <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100">
                  <Search size={20} className="text-slate-400 shrink-0" />
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Zoek in facturen, offertes, contacten, uitgaven…"
                    className="flex-1 text-slate-800 placeholder-slate-400 outline-none bg-transparent text-sm"
                  />
                  {loading && <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" />}
                  <kbd className="hidden sm:inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400">
                    ESC
                  </kbd>
                </div>

                {/* Resultaten */}
                <div ref={listRef} className="max-h-[55vh] overflow-y-auto py-2">
                  {query.trim().length >= 2 && !loading && flat.length === 0 && (
                    <p className="px-4 py-8 text-center text-sm text-slate-400">
                      Geen resultaten voor “{query.trim()}”
                    </p>
                  )}
                  {query.trim().length < 2 && (
                    <p className="px-4 py-8 text-center text-sm text-slate-400">
                      Typ minimaal 2 tekens om te zoeken
                    </p>
                  )}

                  {grouped.map((g) => {
                    const meta = TYPE_META[g.type];
                    return (
                      <div key={g.type} className="mb-1">
                        <p className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                          {meta.label}
                        </p>
                        {g.items.map((r) => {
                          idx += 1;
                          const myIdx = idx;
                          const Icon = meta.icon;
                          const isActive = myIdx === activeIndex;
                          return (
                            <button
                              key={`${r.type}-${r.id}`}
                              data-idx={myIdx}
                              onClick={() => go(r)}
                              onMouseEnter={() => setActiveIndex(myIdx)}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                                isActive ? "bg-brand-50" : "hover:bg-slate-50"
                              }`}
                            >
                              <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
                                <Icon size={16} />
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className="block text-sm font-semibold text-slate-800 truncate">{r.title}</span>
                                {r.subtitle && (
                                  <span className="block text-xs text-slate-400 truncate">{r.subtitle}</span>
                                )}
                              </span>
                              <span className="flex items-center gap-2 shrink-0">
                                {typeof r.amount === "number" && (
                                  <span className="text-xs font-semibold text-slate-600">
                                    {r.currency === "EUR" ? eur(r.amount) : `${eur(r.amount)} ${r.currency}`}
                                  </span>
                                )}
                                {r.status && (
                                  <span className="text-[10px] font-semibold text-slate-400 uppercase">
                                    {nlStatus(r.status)}
                                  </span>
                                )}
                                {r.date && (
                                  <span className="text-xs text-slate-400">{fmtDate(r.date)}</span>
                                )}
                                {isActive && <CornerDownLeft size={14} className="text-brand-500" />}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* Voettekst met sneltoetsen */}
                <div className="flex items-center gap-4 px-4 py-2.5 border-t border-slate-100 bg-slate-50/60 text-[11px] text-slate-400">
                  <span className="flex items-center gap-1">
                    <kbd className="rounded-md border border-slate-200 bg-white px-1 py-0.5 font-semibold">↑↓</kbd> navigeren
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded-md border border-slate-200 bg-white px-1 py-0.5 font-semibold">↵</kbd> openen
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded-md border border-slate-200 bg-white px-1 py-0.5 font-semibold">Ctrl K</kbd> zoeken
                  </span>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}